import { useEffect, useMemo, useState } from 'react';
import { publishScope } from '@/views/handover/paletteStore';
import { displayDossierName } from '@/hooks/useTidyName';
import { useQuery } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { actesDossiers, type ActesDossier, type ActesReference } from '@/integrations/actesApi';
import { DossierModal } from './DossierModal';
import { ParallelImportModal } from './ParallelImport';
import { pillOf } from './pill';
import { ActesAvatar } from './ActesPerson';
import { countryOfRef, dossierStatusLabel, actesEn } from '@/lib/actesCountry';

/**
 * DOSSIERS — one client, one procedure.
 *
 * The shape is the one the French team already worked with, because it was
 * right and they know it: active and closed as two tabs, a search over the
 * client's name, two labelled filters (status, and whose dossier it is — with
 * the two team-wide views that cannot belong to anyone: no lead, no team), a
 * sortable table, and the count of what is on screen. What changed is the
 * paint: the portal's tokens, type and icons, nothing of its own.
 */
type SortKey = 'name' | 'status' | 'pct' | 'received' | 'pending' | 'activity';
type Quick = 'no_lead' | 'no_team' | null;

export function DossiersPanel({ lang, reference, onOpen }: {
  lang: Lang;
  reference: ActesReference;
  onOpen: (id: string) => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [tab, setTab] = useState<'active' | 'closed'>('active');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [owner, setOwner] = useState(reference.me.id);
  const [quick, setQuick] = useState<Quick>(null);
  const [sortKey, setSortKey] = useState<SortKey>('activity');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const list = useQuery<ActesDossier[]>({
    queryKey: ['actes', 'dossiers'],
    queryFn: ({ signal }) => actesDossiers(signal),
    staleTime: 60_000,
  });
  /* ⌘K en Actes: los dossiers por nombre, número y abogado. Discreto: la
     página ya tiene su buscador; esto es la tecla. */
  useEffect(() => {
    const ds = list.data ?? [];
    return publishScope({
      owner: 'actes-dossiers',
      quiet: true,
      kind: 'tickets',
      hint: T('Aller à un dossier — ou demander…', 'Jump to a dossier — or ask…'),
      items: ds.map((d) => ({
        id: d.id,
        title: d.name,
        sub: [dossierStatusLabel(reference, d), d.lead_lawyer_name].filter(Boolean).join(' · '),
        where: d.hubspot_id ? `#${d.hubspot_id}` : '',
        text: [d.name, d.hubspot_id, d.ticket_id, d.lead_lawyer_name, d.status_label_fr].filter(Boolean).join(' · '),
      })),
      onPick: (id) => onOpen(id),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, lang]);
  const rows = list.data ?? [];

  const closedCount = useMemo(() => rows.filter((d) => d.closed_at).length, [rows]);
  const pending = (d: ActesDossier) => (d.actes_total ?? 0) - (d.actes_received ?? 0);
  const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const byId = useMemo(() => new Map(reference.profiles.map((p) => [p.id, p])), [reference.profiles]);

  /* On a dossier = leading it or named on it. Anything narrower hides most of
     a person's work from them. */
  const onDossier = (d: ActesDossier, id: string) =>
    d.lead_lawyer_id === id || (d.member_ids ?? []).includes(id);

  const quickCounts = useMemo(() => {
    const scope = rows.filter((d) => (tab === 'active' ? !d.closed_at : !!d.closed_at));
    return {
      no_lead: scope.filter((d) => !d.lead_lawyer_name).length,
      /* A lead alone is NOT a team: a dossier can have a referring lawyer and
         still be one nobody has been staffed onto. That is the list this
         answers, and eyeballing a Team column over hundreds of rows is not
         an answer. */
      no_team: scope.filter((d) => (d.member_ids ?? []).length === 0).length,
    };
  }, [rows, tab]);

  const filtered = useMemo(() => {
    const q = fold(search.trim());
    const list2 = rows.filter((d) => {
      if (tab === 'active' ? !!d.closed_at : !d.closed_at) return false;
      if (status && d.status_code !== status) return false;
      if (quick === 'no_lead' && d.lead_lawyer_name) return false;
      if (quick === 'no_team' && (d.member_ids ?? []).length > 0) return false;
      if (!quick && owner && !onDossier(d, owner)) return false;
      if (q && !fold(`${d.name} ${d.hubspot_id ?? ''}`).includes(q)) return false;
      return true;
    });
    const val = (d: ActesDossier): number | string => {
      switch (sortKey) {
        case 'name': return fold(d.name);
        case 'status': return fold(d.status_label_fr ?? 'zzz');
        case 'pct': return Number(d.completion_pct) || 0;
        case 'received': return d.actes_received ?? 0;
        case 'pending': return pending(d);
        default: return new Date(d.last_activity ?? d.updated_at).getTime();
      }
    };
    return [...list2].sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, tab, search, status, owner, quick, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'name' ? 'asc' : 'desc'); }
  };

  const dirty = !!search || !!status || !!quick || owner !== '';

  /* CE QUE VOUS CHERCHEZ EST PEUT-ÊTRE AILLEURS.
     Chercher un dossier qui n'est pas dans l'onglet ouvert — ni sous le
     référent choisi — rendait une page vide et s'arrêtait là. On compte ce
     que le seul terme de recherche trouverait partout, et on le propose. */
  const elsewhere = useMemo(() => {
    const q = fold(search.trim());
    if (!q) return 0;
    return rows.filter((d) => fold(`${d.name} ${d.hubspot_id ?? ''}`).includes(q)).length;
  }, [rows, search]);
  const rel = (iso: string) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 3600) return T('à l’instant', 'just now');
    if (diff < 86400) return T(`il y a ${Math.floor(diff / 3600)} h`, `${Math.floor(diff / 3600)} h ago`);
    if (diff < 172800) return T('hier', 'yesterday');
    return new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB');
  };

  /* L'en-tête triable de la maquette : un <button> dans le <th>, la flèche
     dans un `.arw`, et `aria-sort` qui dit au CSS —et au lecteur d'écran— sur
     quelle colonne on est. Le tri lui-même n'a pas bougé d'une ligne. */
  const Th = ({ k, label, hide }: { k: SortKey; label: string; hide?: boolean }) => (
    <th className={hide ? 'hide-s' : undefined}
      aria-sort={sortKey === k ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button type="button" onClick={() => toggleSort(k)}>
        {label}<span className="arw">{sortKey === k ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );

  return (
    <section className="actes-panel v7">
      {/* ── LE BOUTON EST TOUJOURS SUR LA PREMIÈRE LIGNE, AVEC LE TITRE ──
          Il était dans la rangée de pastilles ici, et sur la ligne du titre
          dans DPC et Autorités : d'un écran à l'autre il changeait de hauteur,
          et l'œil devait le rechercher. Les quatre vues ont maintenant la même
          première ligne — le titre à gauche, l'action à droite — et les
          filtres commencent en dessous. */}
      <div className="phead">
        <h2>{T('Dossiers', 'Dossiers')}</h2>
        {/* Portugal: a dossier is born on its nationality case (country.ts on
            the server) — the case page creates it, with Parallel. */}
        {reference.me.isStaff && countryOfRef(reference) === 'PT' && (
          <p className="muted-note" style={{ margin: 0 }}>
            New dossiers are created from the nationality case: open the case → Civil records → Create.
          </p>
        )}
        {reference.me.isStaff && countryOfRef(reference) === 'FR' && (
          <div className="right">
            {/* Lire le formulaire du client vaut mieux que retaper onze fois
                la même réponse : le bouton est à côté du manuel, pas derrière. */}
            <button className="btn" onClick={() => setImporting(true)}>
              <KBIcon name="ai" size={13} /> {T('Depuis Parallel', 'From Parallel')}
            </button>
            <button className="btn btn-p" onClick={() => setCreating(true)}>
              <KBIcon name="plus" size={13} /> {T('Nouveau dossier', 'New dossier')}
            </button>
          </div>
        )}
      </div>

      <div className="pillrow">
        <button className="fp" aria-pressed={tab === 'active'} onClick={() => setTab('active')}>{T('Actifs', 'Active')}</button>
        <button className="fp" aria-pressed={tab === 'closed'} onClick={() => setTab('closed')}>
          {T('Clôturés', 'Closed')}{closedCount > 0 && <span className="n">{closedCount}</span>}
        </button>
      </div>

      <div className="toolbar">
        <label className="srch">
          <KBIcon name="search" size={15} />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={T('Chercher un client…', 'Search a client…')} />
          {search && <button className="x" onClick={() => setSearch('')} aria-label="clear">×</button>}
        </label>
        <span className="vr" />
        <>
          <label className="hint">
            {T('Statut', 'Status')}
            <select className="sel" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{T('Tous', 'All')}</option>
              {reference.dossierStatuses.map((s) => (
                <option key={s.id} value={s.code}>{lang === 'fr' ? s.label_fr : s.label_en}</option>
              ))}
            </select>
          </label>

          {/* Une seule commande répond à « le dossier de qui » : quelqu'un,
              tout le monde, ou la vue d'équipe qui n'est à personne. */}
          <label className="hint">
            {T('Référent', 'Lead')}
            <select
              className="sel"
              value={quick ? `__${quick}__` : owner}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__no_lead__') { setQuick('no_lead'); setOwner(''); }
                else if (v === '__no_team__') { setQuick('no_team'); setOwner(''); }
                else { setQuick(null); setOwner(v); }
              }}
            >
              <option value="">{T('Tout le monde', 'Everyone')}</option>
              {reference.profiles.map((p) => (
                <option key={p.id} value={p.id}>{p.full_name ?? p.email}</option>
              ))}
              <option value="__no_lead__">{T(`Sans référent (${quickCounts.no_lead})`, `No lead (${quickCounts.no_lead})`)}</option>
              <option value="__no_team__">{T(`Sans équipe (${quickCounts.no_team})`, `No team (${quickCounts.no_team})`)}</option>
            </select>
          </label>

          {dirty && (
            <button className="btn btn-s" title={T('Effacer les filtres', 'Clear filters')}
              onClick={() => { setSearch(''); setStatus(''); setOwner(''); setQuick(null); }}>
              <KBIcon name="trash" size={13} /> {T('Effacer', 'Clear')}
            </button>
          )}
          <span className="res mono">{filtered.length} {T('résultats', 'results')}</span>
        </>
      </div>

      {list.isLoading ? (
        <p className="muted-note">{T('Chargement…', 'Loading…')}</p>
      ) : filtered.length === 0 ? (
        <div className="estate">
          <span className="ic"><KBIcon name="documents" size={22} /></span>
          <h3>{T('Aucun dossier', 'No dossiers')}</h3>
          <p>{dirty ? T('Rien ne correspond à ces filtres.', 'Nothing matches these filters.') : T('Créez le premier.', 'Create the first one.')}</p>
          {elsewhere > 0 && (
            <button className="btn btn-p" onClick={() => {
              setStatus(''); setOwner(''); setQuick(null);
              /* Le terme existe : si ce n'est pas ici, c'est dans l'autre
                 onglet — on y va plutôt que de laisser une page vide. */
              const q = fold(search.trim());
              const here = rows.some((d) => (tab === 'active' ? !d.closed_at : !!d.closed_at)
                && fold(`${d.name} ${d.hubspot_id ?? ''}`).includes(q));
              if (!here) setTab(tab === 'active' ? 'closed' : 'active');
            }}>
              <KBIcon name="search" size={13} />
              {T(`Chercher « ${search.trim()} » dans tous les dossiers (${elsewhere})`,
                 `Search “${search.trim()}” across all dossiers (${elsewhere})`)}
            </button>
          )}
          {/* On ÉLARGIT, on ne recommence pas : ce qui a été tapé reste tapé.
              Le bouton n'enlève que les filtres — effacer aussi la recherche,
              c'était demander de la retaper. */}
          {elsewhere === 0 && (!!status || !!quick || owner !== '') && (
            <button className="btn btn-p"
              onClick={() => { setStatus(''); setOwner(''); setQuick(null); }}>
              {T('Élargir : enlever les filtres', 'Widen: drop the filters')}
            </button>
          )}
        </div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                {/* La colonne muette de gauche : un filet de couleur qui dit
                    d'un coup d'œil si le dossier presse. */}
                <th className="cs" />
                <Th k="name" label={T('Dossier', 'Dossier')} />
                <Th k="status" label={T('Statut', 'Status')} />
                <Th k="pct" label={T('Avancement', 'Progress')} />
                <th>{T('Ce qui le retient', 'What is holding it up')}</th>
                <Th k="received" label={T('Reçus', 'Received')} hide />
                <Th k="pending" label={T('En attente', 'Pending')} hide />
                <th className="hide-s">{T('Équipe', 'Team')}</th>
                <Th k="activity" label={T('Activité', 'Activity')} />
                <th className="r" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const p = Math.round(Number(d.completion_pct) || 0);
                const urg = d.blocker_kind === 'expiring' ? 'now' : d.blocker_kind === 'sitting' ? 'soon' : d.blocker_kind ? '' : 'ok';
                const nb = Number(d.blocker_n ?? 0);
                const holds = d.blocker_kind === 'expiring'
                  ? T(`${nb} périment avant le dépôt`, `${nb} expire before you can file`)
                  : d.blocker_kind === 'sitting'
                    ? T(`chez ${d.blocker_who || 'une autorité'} depuis longtemps`, `sitting with ${d.blocker_who || 'an authority'}`)
                    : d.blocker_kind === 'never'
                      ? T(`${nb} jamais demandés`, `${nb} never requested`)
                      : T('rien ne le retient', 'nothing holding it up');
                return (
                  <tr key={d.id} onClick={() => onOpen(d.id)}>
                    <td className="cs"><i className={urg} /></td>
                    <td className="nm-c">
                      {/* Un dossier créé avant que le modèle ne lise son objet
                          a été ENREGISTRÉ avec la chaîne de classement ; on le
                          lit comme partout ailleurs, l'original au survol, et
                          rien de ce qu'une personne a tapé n'est touché. */}
                      <b title={d.name}>{displayDossierName(d.name)}</b>
                      {d.hubspot_id && <span className="chipref">#{d.hubspot_id}</span>}
                    </td>
                    <td>{d.status_label_fr
                      ? <span className={`pill ${pillOf(d.status_color)}`}>{dossierStatusLabel(reference, d)}</span>
                      : <span className="dash">—</span>}</td>
                    <td>
                      <div className="prog">
                        <span className="frac"><b>{d.actes_received}</b> {T('sur', 'of')} {d.actes_total}</span>
                        <span className="bar"><i className={p < 40 ? 'low' : p < 70 ? 'mid' : ''} style={{ width: `${p}%` }} /></span>
                      </div>
                    </td>
                    <td className={urg === 'now' ? undefined : 'sub'}
                      style={urg === 'now' ? { color: 'var(--terra)', fontWeight: 500 } : undefined}>{holds}</td>
                    <td className="hide-s num">{d.actes_received}</td>
                    <td className="hide-s num">{pending(d)}</td>
                    <td className="hide-s">
                      {/* Des visages, pas des noms : une colonne de tableau
                          n'est pas l'endroit pour plier « Geronimo McCluskey »
                          sur trois lignes. Le nom est à un survol. */}
                      <span className="stack">
                        {d.lead_lawyer_id && <ActesAvatar person={byId.get(d.lead_lawyer_id)} />}
                        {(d.member_ids ?? []).filter((m) => m !== d.lead_lawyer_id).slice(0, 3).map((m) => (
                          <ActesAvatar key={m} person={byId.get(m)} />
                        ))}
                        {!d.lead_lawyer_id && (d.member_ids ?? []).length === 0 && <span className="dash">—</span>}
                      </span>
                    </td>
                    <td className="sub num">{rel(d.last_activity ?? d.updated_at)}</td>
                    <td className="r">
                      <div className="rowbtns">
                        <button type="button" className="rb open" title={T('Ouvrir', 'Open')}
                          onClick={(e) => { e.stopPropagation(); onOpen(d.id); }}>→</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {importing && (
        <ParallelImportModal lang={lang} reference={reference}
          onClose={() => setImporting(false)} onCreated={(id) => onOpen(id)} />
      )}
      {creating && (
        <DossierModal
          lang={lang}
          reference={reference}
          onClose={() => setCreating(false)}
          onSaved={(id) => onOpen(id)}
        />
      )}
    </section>
  );
}
